/**
 * TOOL ASSIGNMENT SERVICE
 * 
 * Gestiona la asignación de herramientas terapéuticas a pacientes.
 * Permite que cada terapeuta elija qué herramientas usa y cuáles asigna a cada paciente.
 * 
 * Flujo:
 * 1. Terapeuta habilita herramientas en su cuenta (enabledTools)
 * 2. Terapeuta asigna herramientas específicas a cada paciente (assignedTools)
 * 3. Paciente ve solo las herramientas que tiene asignadas
 */

import { db } from '@/core/config/firebase';
import { Collections } from '@/core/config/collections';
import { Logger } from '@/core/utils';
import { auditLogService } from '@/core/security/audit-log.service';
import { PatientToolAssignment } from '@/core/types/models/therapist/therapist.model';
import { INITIAL_THERAPEUTIC_TOOLS, ToolStatus } from '@/core/types/models/content/therapeutic-tools.model';

const logger = Logger.getLogger('ToolAssignmentService');

// Herramientas disponibles por defecto (para usuarios sin terapeuta)
export const DEFAULT_USER_TOOLS = ['wheel-of-life'];

// Todas las herramientas disponibles en el sistema
export const AVAILABLE_TOOLS = INITIAL_THERAPEUTIC_TOOLS.filter(
  t => t.status === ToolStatus.AVAILABLE || t.status === ToolStatus.BETA
);

// ============================================
// FEATURE FLAG: Todas las tools habilitadas por defecto
// Hasta que tengamos masa crítica de terapeutas, todas las
// herramientas están habilitadas sin necesidad de configuración
// ============================================
const ALL_TOOLS_ENABLED_BY_DEFAULT = true;

export interface ToolInfo {
  slug: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  requiresPatient: boolean;
}

class ToolAssignmentService {
  private readonly therapistAccountsCollection = Collections.THERAPIST_ACCOUNTS;
  private readonly relationshipsCollection = Collections.THERAPIST_PATIENTS;

  // ============================================
  // TERAPEUTA: Gestión de herramientas habilitadas
  // ============================================

  /**
   * Obtener herramientas habilitadas por el terapeuta
   * 
   * NOTA: Mientras ALL_TOOLS_ENABLED_BY_DEFAULT sea true,
   * todas las herramientas están habilitadas automáticamente.
   * Esto simplifica la experiencia hasta tener masa crítica de terapeutas.
   */
  async getTherapistEnabledTools(therapistId: string): Promise<string[]> {
    try {
      // Si todas las tools están habilitadas por defecto, devolver todas
      if (ALL_TOOLS_ENABLED_BY_DEFAULT) {
        return AVAILABLE_TOOLS.map(t => t.slug);
      }

      const snapshot = await db.collection(this.therapistAccountsCollection)
        .where('userId', '==', therapistId)
        .limit(1)
        .get();

      if (snapshot.empty) {
        // Si no hay cuenta, devolver todas las tools por defecto
        logger.info('Therapist account not found, returning all tools as enabled', { therapistId });
        return AVAILABLE_TOOLS.map(t => t.slug);
      }

      const data = snapshot.docs[0].data();
      // Si no hay configuración de tools, devolver todas como habilitadas
      if (!data.enabledTools || data.enabledTools.length === 0) {
        return AVAILABLE_TOOLS.map(t => t.slug);
      }
      
      return data.enabledTools;
    } catch (error) {
      logger.error('Failed to get therapist enabled tools', error);
      // En caso de error, devolver todas las tools para no bloquear la experiencia
      return AVAILABLE_TOOLS.map(t => t.slug);
    }
  }

  /**
   * Habilitar/deshabilitar una herramienta para el terapeuta
   * 
   * NOTA: Mientras ALL_TOOLS_ENABLED_BY_DEFAULT sea true,
   * esta función no hace cambios reales pero registra la intención.
   * Esto permite una transición suave cuando desactivemos el flag.
   */
  async toggleTherapistTool(therapistId: string, toolSlug: string, enabled: boolean): Promise<void> {
    try {
      // Si todas las tools están habilitadas por defecto, solo logear pero no fallar
      if (ALL_TOOLS_ENABLED_BY_DEFAULT) {
        logger.info('Tool toggle ignored (all tools enabled by default)', { therapistId, toolSlug, enabled });
        
        // Aún así registrar en audit log para trazabilidad
        await auditLogService.log({
          userId: therapistId,
          userRole: 'therapist',
          action: enabled ? 'enable' : 'disable',
          resourceType: 'therapeutic_tool',
          resourceId: toolSlug,
          details: { toolSlug, enabled, note: 'All tools enabled by default' }
        });
        
        return;
      }

      const snapshot = await db.collection(this.therapistAccountsCollection)
        .where('userId', '==', therapistId)
        .limit(1)
        .get();

      // Si no existe la cuenta, crear una con las tools configuradas
      if (snapshot.empty) {
        logger.info('Therapist account not found, skipping toggle', { therapistId, toolSlug });
        // No lanzar error, simplemente logear y retornar
        // El toggle no tiene efecto real cuando todas las tools están habilitadas
        await auditLogService.log({
          userId: therapistId,
          userRole: 'therapist',
          action: enabled ? 'enable' : 'disable',
          resourceType: 'therapeutic_tool',
          resourceId: toolSlug,
          details: { toolSlug, enabled, note: 'Account not found, all tools available' }
        });
        return;
      }

      const docRef = snapshot.docs[0].ref;
      const currentTools: string[] = snapshot.docs[0].data().enabledTools || AVAILABLE_TOOLS.map(t => t.slug);

      let updatedTools: string[];
      if (enabled) {
        updatedTools = currentTools.includes(toolSlug) 
          ? currentTools 
          : [...currentTools, toolSlug];
      } else {
        updatedTools = currentTools.filter(t => t !== toolSlug);
      }

      await docRef.update({
        enabledTools: updatedTools,
        updatedAt: new Date().toISOString()
      });

      await auditLogService.log({
        userId: therapistId,
        userRole: 'therapist',
        action: enabled ? 'enable' : 'disable',
        resourceType: 'therapeutic_tool',
        resourceId: toolSlug,
        details: { toolSlug, enabled }
      });

      logger.info('Therapist tool toggled', { therapistId, toolSlug, enabled });
    } catch (error) {
      logger.error('Failed to toggle therapist tool', error);
      // No lanzar error para no bloquear la experiencia
      // Las tools seguirán funcionando por defecto
      logger.info('Toggle failed but all tools remain available', { therapistId, toolSlug });
    }
  }

  /**
   * Establecer todas las herramientas habilitadas del terapeuta
   * 
   * NOTA: Mientras ALL_TOOLS_ENABLED_BY_DEFAULT sea true,
   * esta función solo logea la intención pero no afecta el comportamiento.
   */
  async setTherapistEnabledTools(therapistId: string, toolSlugs: string[]): Promise<void> {
    try {
      // Si todas las tools están habilitadas por defecto, solo logear
      if (ALL_TOOLS_ENABLED_BY_DEFAULT) {
        logger.info('Set tools ignored (all tools enabled by default)', { therapistId, count: toolSlugs.length });
        return;
      }

      const snapshot = await db.collection(this.therapistAccountsCollection)
        .where('userId', '==', therapistId)
        .limit(1)
        .get();

      if (snapshot.empty) {
        logger.info('Therapist account not found, skipping set tools', { therapistId });
        return;
      }

      await snapshot.docs[0].ref.update({
        enabledTools: toolSlugs,
        updatedAt: new Date().toISOString()
      });

      logger.info('Therapist enabled tools updated', { therapistId, count: toolSlugs.length });
    } catch (error) {
      logger.error('Failed to set therapist enabled tools', error);
      // No lanzar error para no bloquear la experiencia
      logger.info('Set tools failed but all tools remain available', { therapistId });
    }
  }

  // ============================================
  // PACIENTE: Asignación de herramientas
  // ============================================

  /**
   * Obtener herramientas asignadas a un paciente por su terapeuta
   */
  async getPatientAssignedTools(patientId: string): Promise<PatientToolAssignment[]> {
    try {
      // Buscar relación activa con terapeuta
      const snapshot = await db.collection(this.relationshipsCollection)
        .where('patientId', '==', patientId)
        .where('status', '==', 'active')
        .limit(1)
        .get();

      if (snapshot.empty) {
        logger.info('No active therapist relationship found', { patientId });
        return [];
      }

      const data = snapshot.docs[0].data();
      return data.assignedTools || [];
    } catch (error) {
      logger.error('Failed to get patient assigned tools', error);
      throw error;
    }
  }

  /**
   * Verificar si un paciente tiene una herramienta específica asignada
   */
  async hasToolAssigned(patientId: string, toolSlug: string): Promise<boolean> {
    try {
      const assignedTools = await this.getPatientAssignedTools(patientId);
      const tool = assignedTools.find(t => t.toolSlug === toolSlug);
      return tool?.enabled === true;
    } catch (error) {
      logger.error('Failed to check tool assignment', error);
      return false;
    }
  }

  /**
   * Obtener herramientas que el paciente puede ver
   * Si tiene terapeuta: solo las asignadas
   * Si no tiene terapeuta: herramientas por defecto
   */
  async getPatientVisibleTools(patientId: string): Promise<ToolInfo[]> {
    try {
      const assignedTools = await this.getPatientAssignedTools(patientId);
      
      // Si no tiene herramientas asignadas (no tiene terapeuta o no le han asignado nada)
      if (assignedTools.length === 0) {
        // Devolver herramientas por defecto
        return AVAILABLE_TOOLS
          .filter(t => DEFAULT_USER_TOOLS.includes(t.slug))
          .map(t => ({
            slug: t.slug,
            name: t.name,
            description: t.description,
            icon: t.icon,
            category: t.category,
            requiresPatient: t.requiresPatient
          }));
      }

      // Filtrar solo las habilitadas
      const enabledSlugs = assignedTools
        .filter(t => t.enabled)
        .map(t => t.toolSlug);

      return AVAILABLE_TOOLS
        .filter(t => enabledSlugs.includes(t.slug))
        .map(t => ({
          slug: t.slug,
          name: t.name,
          description: t.description,
          icon: t.icon,
          category: t.category,
          requiresPatient: t.requiresPatient
        }));
    } catch (error) {
      logger.error('Failed to get patient visible tools', error);
      // En caso de error, devolver herramientas por defecto
      return AVAILABLE_TOOLS
        .filter(t => DEFAULT_USER_TOOLS.includes(t.slug))
        .map(t => ({
          slug: t.slug,
          name: t.name,
          description: t.description,
          icon: t.icon,
          category: t.category,
          requiresPatient: t.requiresPatient
        }));
    }
  }

  /**
   * Asignar una herramienta a un paciente
   */
  async assignToolToPatient(
    therapistId: string,
    patientId: string,
    relationshipId: string,
    toolSlug: string,
    config?: Record<string, any>
  ): Promise<void> {
    try {
      const docRef = db.collection(this.relationshipsCollection).doc(relationshipId);
      const doc = await docRef.get();

      if (!doc.exists) {
        throw new Error('Relationship not found');
      }

      const data = doc.data()!;
      
      // Verificar que la herramienta existe y está disponible
      const toolExists = AVAILABLE_TOOLS.some(t => t.slug === toolSlug);
      if (!toolExists) {
        throw new Error('Tool does not exist or is not available');
      }
      
      // Cuando ALL_TOOLS_ENABLED_BY_DEFAULT es true, no verificamos la config del terapeuta
      // ya que todas las herramientas están disponibles automáticamente

      const currentAssignments: PatientToolAssignment[] = data.assignedTools || [];
      const existingIndex = currentAssignments.findIndex(t => t.toolSlug === toolSlug);

      const newAssignment: PatientToolAssignment = {
        toolSlug,
        enabled: true,
        assignedAt: new Date().toISOString(),
        ...(config !== undefined && { config })
      };

      let updatedAssignments: PatientToolAssignment[];
      if (existingIndex >= 0) {
        updatedAssignments = [...currentAssignments];
        updatedAssignments[existingIndex] = newAssignment;
      } else {
        updatedAssignments = [...currentAssignments, newAssignment];
      }

      await docRef.update({
        assignedTools: updatedAssignments,
        updatedAt: new Date().toISOString()
      });

      await auditLogService.log({
        userId: therapistId,
        userRole: 'therapist',
        action: 'assign',
        resourceType: 'patient_tool',
        resourceId: `${patientId}_${toolSlug}`,
        details: { patientId, toolSlug, relationshipId }
      });

      logger.info('Tool assigned to patient', { therapistId, patientId, toolSlug });
    } catch (error) {
      logger.error('Failed to assign tool to patient', error);
      throw error;
    }
  }

  /**
   * Desasignar una herramienta de un paciente
   */
  async unassignToolFromPatient(
    therapistId: string,
    patientId: string,
    relationshipId: string,
    toolSlug: string
  ): Promise<void> {
    try {
      const docRef = db.collection(this.relationshipsCollection).doc(relationshipId);
      const doc = await docRef.get();

      if (!doc.exists) {
        throw new Error('Relationship not found');
      }

      const data = doc.data()!;
      const currentAssignments: PatientToolAssignment[] = data.assignedTools || [];
      const existingIndex = currentAssignments.findIndex(t => t.toolSlug === toolSlug);

      if (existingIndex < 0) {
        logger.warn('Tool not assigned to patient', { patientId, toolSlug });
        return;
      }

      // Marcar como deshabilitado en lugar de eliminar (para historial)
      const updatedAssignments = [...currentAssignments];
      updatedAssignments[existingIndex] = {
        ...updatedAssignments[existingIndex],
        enabled: false
      };

      await docRef.update({
        assignedTools: updatedAssignments,
        updatedAt: new Date().toISOString()
      });

      await auditLogService.log({
        userId: therapistId,
        userRole: 'therapist',
        action: 'unassign',
        resourceType: 'patient_tool',
        resourceId: `${patientId}_${toolSlug}`,
        details: { patientId, toolSlug, relationshipId }
      });

      logger.info('Tool unassigned from patient', { therapistId, patientId, toolSlug });
    } catch (error) {
      logger.error('Failed to unassign tool from patient', error);
      throw error;
    }
  }

  /**
   * Asignar múltiples herramientas a un paciente de una vez
   */
  async setPatientTools(
    therapistId: string,
    patientId: string,
    relationshipId: string,
    toolSlugs: string[]
  ): Promise<void> {
    try {
      const docRef = db.collection(this.relationshipsCollection).doc(relationshipId);
      const doc = await docRef.get();

      if (!doc.exists) {
        throw new Error('Relationship not found');
      }

      // Verificar que todas las herramientas existen y están disponibles
      const availableSlugs = AVAILABLE_TOOLS.map(t => t.slug);
      const invalidTools = toolSlugs.filter(slug => !availableSlugs.includes(slug));
      
      if (invalidTools.length > 0) {
        throw new Error(`Tools do not exist: ${invalidTools.join(', ')}`);
      }
      
      // Cuando ALL_TOOLS_ENABLED_BY_DEFAULT es true, no verificamos la config del terapeuta

      const now = new Date().toISOString();
      const assignments: PatientToolAssignment[] = toolSlugs.map(slug => ({
        toolSlug: slug,
        enabled: true,
        assignedAt: now
      }));

      await docRef.update({
        assignedTools: assignments,
        updatedAt: now
      });

      await auditLogService.log({
        userId: therapistId,
        userRole: 'therapist',
        action: 'update',
        resourceType: 'patient_tools',
        resourceId: patientId,
        details: { patientId, toolSlugs, relationshipId }
      });

      logger.info('Patient tools updated', { therapistId, patientId, count: toolSlugs.length });
    } catch (error) {
      logger.error('Failed to set patient tools', error);
      throw error;
    }
  }

  // ============================================
  // UTILIDADES
  // ============================================

  /**
   * Obtener información de todas las herramientas disponibles
   */
  getAllAvailableTools(): ToolInfo[] {
    return AVAILABLE_TOOLS.map(t => ({
      slug: t.slug,
      name: t.name,
      description: t.description,
      icon: t.icon,
      category: t.category,
      requiresPatient: t.requiresPatient
    }));
  }

  /**
   * Obtener información de una herramienta por slug
   */
  getToolBySlug(slug: string): ToolInfo | null {
    const tool = AVAILABLE_TOOLS.find(t => t.slug === slug);
    if (!tool) return null;
    
    return {
      slug: tool.slug,
      name: tool.name,
      description: tool.description,
      icon: tool.icon,
      category: tool.category,
      requiresPatient: tool.requiresPatient
    };
  }

  /**
   * Submit a tool request from a therapist
   */
  async submitToolRequest(therapistId: string, data: {
    toolName: string;
    toolDescription: string;
    therapeuticApproach?: string;
    desiredFeatures?: string[];
  }): Promise<void> {
    const docData: Record<string, any> = {
      userId: therapistId,
      toolName: data.toolName,
      toolDescription: data.toolDescription,
      status: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    if (data.therapeuticApproach) {
      docData.therapeuticApproach = data.therapeuticApproach;
    }
    if (data.desiredFeatures && data.desiredFeatures.length > 0) {
      docData.desiredFeatures = data.desiredFeatures;
    }
    await db.collection(Collections.TOOL_REQUESTS).add(docData);
    logger.info('Tool request submitted', { therapistId, toolName: data.toolName });
  }
}

export const toolAssignmentService = new ToolAssignmentService();

